import { dropLast, map, path, uniq } from 'ramda'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { useChecklistForm, useSelectedQuestion } from '../../../_form/ChecklistFormHandler'
import AnnotationSection from './sections/AnnotationSection'
import BasicConfigSection from './sections/BasicConfigSection'
import BooleanSection from './sections/BooleanSection'
import Border from '../../../../../../components/helpers/Border'
import AutofillSection from './sections/AutofillSection'
import DefaultValueSection from './sections/DefaultValueSection'
import FilterSection from './sections/FilterSection'
import GeneralSection from './sections/GeneralSection'
import InfoSection from './sections/InfoSection'
import NonComplianceSection from './sections/NonComplianceSection'
import NumberSection from './sections/NumberSection'
import OptionsSection from './sections/OptionsSection'
import PhotoSection from './sections/PhotoSection'
import Scroll from '../../../../../../components/helpers/Scroll'
import Separator from '../../../../../../packages/kiti/components/helpers/Separator'
import SeparatorWrapper from '../../../../../../components/helpers/SeparatorWrapper'
import Sticky from '../../../../../../components/helpers/Sticky'
import TagsSection from './sections/TagsSection'
import IntegrationSection from './sections/IntegrationSection'
import Text, { Title } from '../../../../../../packages/kiti/components/presentation/Text'
import CalcSection from './sections/CalcSection'
import MaskSection from './sections/MaskSection'
import FieldsLoader from '../../../_load/FieldsLoader'
import { usePlaceTypes } from '../../../../../../utils/helpers/placeTypes'
import StockOperationTypeSection from './sections/StockOperationTypeSection'
import FilterByFieldValueSection from './sections/filterByFieldValueSection/FilterByFieldValueSection'
import LegacyWarning from './sections/LegacyWarning'
import ValidationCodeSection from './sections/ValidationCodeSection'

const QuestionConfigForm = () => {
  const { t } = useTranslation('editChecklist')
  const selectedQuestion = useSelectedQuestion()
  const { selectedQuestionPath, ...form } = useChecklistForm() || {}

  const siblingsPath = dropLast(1, selectedQuestionPath)
  const questions = path(siblingsPath, form) || []

  const [types] = usePlaceTypes()
  const allPlaces = types.map(item => item.key) || []

  const ORIGINS_NAME_FOR_REQUEST = {
    asset: 'assets',
    user: 'contracts'
  }

  const uniqueOrigins = (questions.length && uniq(map(item => item.questionType?.name, questions))) || []
  const assetAndUserOrigins =
    (uniqueOrigins.length &&
      uniqueOrigins
        .filter(questionTypeName => ['asset', 'user'].includes(questionTypeName))
        .map(questionTypeName => ORIGINS_NAME_FOR_REQUEST[questionTypeName])) ||
    []

  const filteredPlaces =
    (questions.length && [
      ...new Set(
        questions
          .flatMap(question => (question.data?.filter?.placeLevelIds || []).map(level => level.type))
          .filter(Boolean)
      )
    ]) ||
    []

  const placeOrigins = filteredPlaces.length ? filteredPlaces : allPlaces

  const origins = assetAndUserOrigins.concat(placeOrigins)

  if (!selectedQuestion.reference)
    return (
      <SeparatorWrapper usePadding weight={12}>
        <Text align="center">{t('selectQuestionToEdit')}</Text>
      </SeparatorWrapper>
    )

  const title = t('fieldTypes.' + path(['questionType', 'name'], selectedQuestion), {
    ns: 'common'
  })

  return (
    <>
      <Sticky>
        <Scroll maxHeight="calc(100vh - 7.5rem)">
          <Border noTop noRight noLeft noRadius>
            <SeparatorWrapper usePadding weight={4}>
              <LegacyWarning questionType={selectedQuestion?.questionType.name} />
              <Title noMargin size="h5">
                {title}
              </Title>
            </SeparatorWrapper>
          </Border>

          <SeparatorWrapper weight={4}>
            <GeneralSection />
            <InfoSection />
            <OptionsSection />
            <StockOperationTypeSection />
            <BasicConfigSection />
            <FieldsLoader origin={origins}>
              <CalcSection />
            </FieldsLoader>
            <NumberSection />
            <FilterSection />
            <FilterByFieldValueSection />
            <AutofillSection />
            <AnnotationSection />
            <PhotoSection />
            <BooleanSection />
            <ValidationCodeSection />
            {selectedQuestion.questionType.name !== 'stock' && (
              <FieldsLoader origin={origins}>
                <DefaultValueSection />
              </FieldsLoader>
            )}
            <NonComplianceSection />
            <TagsSection />
            <MaskSection />
            <IntegrationSection />
            <Separator weight={120} />
          </SeparatorWrapper>
        </Scroll>
      </Sticky>
    </>
  )
}

export default QuestionConfigForm
